import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "react-final-form";
import { Formatter } from "../../services/formatter/formatter.service";
import UiButton from "../button/Button";
import UiInputText from "../input-text/InputText.component";
import { Panel } from "primereact/panel";
import TwilioVoiceService from "../../services/twilio/twilio-voice.service";
import { UiFieldMessageProps } from "../../models/components/field-message.model";
import UiFieldMessage from "../field-message/FieldMessage.component";
import { Device } from "@twilio/voice-sdk";
import { getSessionStorageUser } from "../../services/session/auth.service";
import { CallManagementEvent } from "../../models/call-management.model";
import { OverlayPanel } from "primereact/overlaypanel";
import { Dialog } from "primereact/dialog";
import { useMediaQuery } from "react-responsive";
import { BsTelephoneFill } from "react-icons/bs";
import { BiCopy } from "react-icons/bi";
import { FaCheck } from "react-icons/fa";
import UiInputSwitch from "../input-switch/UiInputSwitch.component";
import {
	TRANSLATION_LINE_COPY_VALUE,
	TRANSLATION_LINE_DISPLAY,
} from "../../utils/translation-line";

const UiDialPad = () => {
	const { t } = useTranslation("common");
	const isDesktop = useMediaQuery({ query: "(min-width: 992px)" });

	const [isDisabled, setIsDisabled] = useState<boolean>(true);
	const [isGroupCall, setIsGroupCall] = useState<boolean>(false);
	const [hasActiveCall, setHasActiveCall] = useState<boolean>(false);
	const [isCurrentCallConference, setIsCurrentCallConference] = useState<boolean>(false);
	const [showConflictDialog, setShowConflictDialog] = useState<boolean>(false);
	const [pendingCallData, setPendingCallData] = useState<{ phoneNumberTo: string } | null>(null);
	const [hasCopiedTranslationLine, setHasCopiedTranslationLine] = useState<boolean>(false);

	const op = useRef<OverlayPanel>(null);

	const setKeypadTemplate = (): React.ReactNode => <></>;

	const keypadKeys: { [x: string]: { value: string; subValue?: string }[] } = {
		row1: [
			{ value: "1", subValue: "-" },
			{ value: "2", subValue: "ABC" },
			{ value: "3", subValue: "DEF" },
		],
		row2: [
			{ value: "4", subValue: "GHI" },
			{ value: "5", subValue: "JKL" },
			{ value: "6", subValue: "MNO" },
		],
		row3: [
			{ value: "7", subValue: "PQRS" },
			{ value: "8", subValue: "TUV" },
			{ value: "9", subValue: "WXYZ" },
		],
		row4: [{ value: "*" }, { value: "0", subValue: "+" }, { value: "#" }],
	};

	const setKeypadKeyTemplate = (
		key: string,
		keyRow: { value: string; subValue?: string }[]
	): React.ReactNode => {
		return (
			<div
				key={key}
				className="dialkey-row"
			>
				{keyRow.map((key) => (
					<div
						className="key-item"
						key={key.value}
						onClick={() => appendToPhoneNumber(key.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								appendToPhoneNumber(key.value);
							}
						}}
						role="button"
						tabIndex={0}
					>
						<span className="key-value">{key.value}</span>
						{key.subValue && <span className="key-subvalue">{key.subValue}</span>}
					</div>
				))}
			</div>
		);
	};

	const setKeypadRowTemplate = (): React.ReactNode[] => {
		const rows: React.ReactNode[] = [];
		Object.entries(keypadKeys).forEach(([key, val]) => {
			const row: React.ReactNode = setKeypadKeyTemplate(key, val);
			rows.push(row);
		});
		return rows;
	};

	/**
	 * Phone number
	 */

	const [formData, setFormData] = useState<{
		phoneNumberTo: string;
		formMessage?: UiFieldMessageProps;
	}>({
		phoneNumberTo: "+1",
		formMessage: undefined,
	});

	const appendToPhoneNumber = (value: string) => {
		setFormData({ ...formData, phoneNumberTo: formData.phoneNumberTo + value });
	};

	const setPhoneNumber = (value: string) => {
		setFormData({ ...formData, phoneNumberTo: value });
	};

	// Outbound call (standard or group conference)
	const executeCall = (phoneNumberTo: string): void => {
		op.current?.hide();
		if (isGroupCall) {
			const coachPhone = `${getSessionStorageUser()?.countryCode}${getSessionStorageUser()?.phone}`;
			TwilioVoiceService.startGroupCall(
				Formatter.phoneNumber(phoneNumberTo, true) ?? phoneNumberTo,
				coachPhone
			);
		} else {
			TwilioVoiceService.outboundCall(phoneNumberTo);
		}
		formData.phoneNumberTo = "+1";
		setIsGroupCall(false);
		setFormData({ ...formData, formMessage: undefined });
	};

	const call = (data: { phoneNumberTo: string }): void => {
		if (hasActiveCall && !isCurrentCallConference) {
			setPendingCallData(data);
			setShowConflictDialog(true);
			return;
		}
		executeCall(data.phoneNumberTo);
	};

	const handleConflictConfirm = (): void => {
		setShowConflictDialog(false);
		if (!pendingCallData) return;
		const coachPhone = isGroupCall
			? `${getSessionStorageUser()?.countryCode}${getSessionStorageUser()?.phone}`
			: undefined;
		TwilioVoiceService.hangUpAndCall(
			Formatter.phoneNumber(pendingCallData.phoneNumberTo, true) ??
				pendingCallData.phoneNumberTo,
			isGroupCall,
			coachPhone
		);
		formData.phoneNumberTo = "+1";
		setIsGroupCall(false);
		setFormData({ ...formData, formMessage: undefined });
		setPendingCallData(null);
	};

	// Copy the number WITHOUT the +1 country code — the field above already has it.
	const handleCopyTranslationLine = (): void => {
		navigator.clipboard.writeText(TRANSLATION_LINE_COPY_VALUE);
		setHasCopiedTranslationLine(true);
		setTimeout(() => setHasCopiedTranslationLine(false), 3000);
	};

	useEffect(() => {
		TwilioVoiceService.callManagementSubject.subscribe((event?: CallManagementEvent) => {
			// Device availability: is registered and is not on an active call
			if (event) {
				setIsDisabled(event?.callManagementState.device?.state !== Device.State.Registered);
				setHasActiveCall(event.callManagementState.calls.length > 0);
				setIsCurrentCallConference(
					event.callManagementState.calls[0]?.state.isConference ?? false
				);
			}
		});
	}, []);

	return (
		<div className="k-dialpad-container">
			{/* Phone icon (clickable) */}
			<UiButton
				icon={<BsTelephoneFill />}
				className={isDesktop ? "p-button-rounded p-button-sm" : "call-responsive"}
				label={isDesktop ? undefined : "UI_COMPONENTS.DIALPAD.TITLE"}
				onClick={(e) => op.current?.toggle(e)}
				aria-haspopup
				aria-controls="overlay_panel"
				disabled={!formData.phoneNumberTo || isDisabled}
			></UiButton>

			<OverlayPanel
				className="k-dialpad-overlay"
				ref={op}
			>
				{/* Calling from */}
				<div className="dial-from-container">
					<span className="dial-from-text">{t("UI_COMPONENTS.DIALPAD.FROM")}</span>
					<span className="dial-from-number">
						{Formatter.phoneNumber(
							`${getSessionStorageUser()?.countryCode}${getSessionStorageUser()?.phone}`
						)}
					</span>
				</div>

				<div className="dial-to-container">
					<Form
						onSubmit={call}
						initialValues={formData}
						render={({ handleSubmit }) => (
							<form onSubmit={handleSubmit}>
								<div className="dial-to-input-container">
									{/* Phone number (input) */}
									<UiInputText
										id="phoneNumberTo"
										placeholder="UI_COMPONENTS.DIALPAD.TO_PLACEHOLDER"
										label="UI_COMPONENTS.DIALPAD.TO_LABEL"
										name="phoneNumberTo"
										onChange={(e) => setPhoneNumber(e.target.value)}
										disabled={isDisabled}
									></UiInputText>

									{/* Make outbound call */}
									<UiButton
										className="p-button-rounded"
										icon={<BsTelephoneFill />}
										disabled={!formData.phoneNumberTo || isDisabled}
									/>
								</div>

								{/* Dialpad messages (error, info, etc.) */}
								{formData?.formMessage && (
									<div className="mb-4">
										<UiFieldMessage {...formData?.formMessage}></UiFieldMessage>
									</div>
								)}

								{/* Keypad */}
								<div className="dial-to-dialpad-container">
									<Panel
										headerTemplate={setKeypadTemplate}
										className="dial-to-keypad-container"
									>
										{setKeypadRowTemplate()}
									</Panel>
								</div>

								{/* Group Call toggle — only shown when there is no active call */}
								{!hasActiveCall && (
									<div className="k-group-call-toggle">
										<div className="k-group-call-text">
											<label
												htmlFor="group-call-toggle"
												className="k-group-call-title"
											>
												{t("UI_COMPONENTS.DIALPAD.GROUP_CALL")}
											</label>
											<span className="k-group-call-subtitle">
												{t("UI_COMPONENTS.DIALPAD.GROUP_CALL_SUBTITLE")}
											</span>
										</div>
										<UiInputSwitch
											id="group-call-toggle"
											name="groupCallToggle"
											checked={isGroupCall}
											onChange={(e) => setIsGroupCall(e.value)}
											disabled={isDisabled}
											inputId="group-call-toggle"
										/>
									</div>
								)}

								{/* Translation (interpreter) line — always available reference */}
								<div className="k-translation-line">
									<div className="k-translation-line-text">
										<span className="k-translation-line-title">
											{t("UI_COMPONENTS.DIALPAD.TRANSLATION_LINE")}
										</span>
										<span className="k-translation-line-number">
											{TRANSLATION_LINE_DISPLAY}
										</span>
									</div>
									<UiButton
										type="button"
										className="p-button-outlined p-button-xs"
										label={
											hasCopiedTranslationLine
												? "UI_COMPONENTS.BUTTONS.COPIED"
												: "UI_COMPONENTS.BUTTONS.COPY"
										}
										icon={hasCopiedTranslationLine ? <FaCheck /> : <BiCopy />}
										onClick={handleCopyTranslationLine}
									/>
								</div>
							</form>
						)}
					/>
				</div>
			</OverlayPanel>

			{/* Active call conflict dialog */}
			<Dialog
				header={t("UI_COMPONENTS.PHONE_CALLER.ACTIVE_CALL_CONFLICT.TITLE")}
				visible={showConflictDialog}
				onHide={() => setShowConflictDialog(false)}
				draggable={false}
				resizable={false}
				className="k-call-confirm-dialog"
				footer={
					<div className="k-call-confirm-footer">
						<UiButton
							label="UI_COMPONENTS.PHONE_CALLER.ACTIVE_CALL_CONFLICT.CANCEL"
							className="p-button-rounded p-button-outlined"
							onClick={() => setShowConflictDialog(false)}
						/>
						<UiButton
							label="UI_COMPONENTS.PHONE_CALLER.ACTIVE_CALL_CONFLICT.CONFIRM"
							className="p-button-rounded p-button-danger"
							icon={<BsTelephoneFill />}
							onClick={handleConflictConfirm}
						/>
					</div>
				}
				focusOnShow={false}
			>
				<p className="k-call-confirm-message">
					{t("UI_COMPONENTS.PHONE_CALLER.ACTIVE_CALL_CONFLICT.MESSAGE")}
				</p>
			</Dialog>
		</div>
	);
};
export default UiDialPad;
